/**
 * Partner Notification Repository
 * Extends BaseRepository for partner notification data access
 */

import { COLLECTIONS } from '../domain/collections';
import {
    BaseRepository,
    BaseEntity
} from '../domain/services/BaseRepository';
import {
    collection,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    getDocs,
    doc as firestoreDoc,
    updateDoc,
    addDoc,
    QueryDocumentSnapshot,
    DocumentData
} from 'firebase/firestore';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';

export type NotificationType =
    | 'new_order'
    | 'order_status_change'
    | 'product_approved'
    | 'product_rejected'
    | 'review_received'
    | 'question_received'
    | 'commission_paid'
    | 'withdrawal_completed'
    | 'shop_verified'
    | 'membership_expiring'
    | 'low_stock'
    | 'system';

export interface PartnerNotificationEntity extends BaseEntity {
    partnerId: string;
    type: NotificationType;
    title: string;
    message: string;
    data?: Record<string, string | number | boolean | null | undefined>;
    isRead: boolean;
    isUrgent: boolean;
    readAt?: string;
}

class PartnerNotificationRepository extends BaseRepository<PartnerNotificationEntity> {
    protected collectionName = COLLECTIONS.PARTNER_NOTIFICATIONS;

    protected toEntity(doc: QueryDocumentSnapshot<DocumentData>): PartnerNotificationEntity {
        const data = doc.data();
        return {
            id: doc.id,
            partnerId: (data.partnerId as string) || '',
            type: (data.type as NotificationType) || 'system',
            title: (data.title as string) || '',
            message: (data.message as string) || '',
            data: data.data as Record<string, string | number | boolean | null | undefined> | undefined,
            isRead: !!data.isRead,
            isUrgent: !!data.isUrgent,
            readAt: data.readAt as string | undefined,
            createdAt: (data.createdAt as string) || new Date().toISOString(),
            updatedAt: (data.updatedAt as string) || new Date().toISOString()
        };
    }

    protected toDocument(data: Partial<PartnerNotificationEntity>): DocumentData {
        const { id, createdAt, updatedAt, ...rest } = data as Record<string, unknown>;
        void id;
        void createdAt;
        void updatedAt;
        return rest as DocumentData;
    }

    async getNotifications(
        partnerId: string,
        options: {
            limit?: number;
            onlyUnread?: boolean;
            type?: NotificationType;
        } = {}
    ): Promise<PartnerNotificationEntity[]> {
        try {
            const constraints = [
                where('partnerId', '==', partnerId),
                orderBy('createdAt', 'desc'),
                firestoreLimit(options.limit || 50)
            ];

            if (options.onlyUnread) {
                constraints.push(where('isRead', '==', false));
            }

            if (options.type) {
                constraints.push(where('type', '==', options.type));
            }

            const q = query(this.collection, ...constraints);
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
        } catch (error) {
            logger.error('[PartnerNotificationRepository] Error fetching notifications', error);
            return [];
        }
    }

    async getUnreadCount(partnerId: string): Promise<number> {
        try {
            const q = query(
                this.collection,
                where('partnerId', '==', partnerId),
                where('isRead', '==', false)
            );
            const snapshot = await getDocs(q);
            return snapshot.size;
        } catch (error) {
            logger.error('[PartnerNotificationRepository] Error fetching unread count', error);
            return 0;
        }
    }

    async markAsRead(notificationId: string): Promise<boolean> {
        try {
            await updateDoc(firestoreDoc(this.collection, notificationId), {
                isRead: true,
                readAt: new Date().toISOString()
            });
            return true;
        } catch (error) {
            logger.error('[PartnerNotificationRepository] Error marking notification as read', error);
            return false;
        }
    }

    async markAllAsRead(partnerId: string): Promise<boolean> {
        try {
            const q = query(
                this.collection,
                where('partnerId', '==', partnerId),
                where('isRead', '==', false)
            );
            const snapshot = await getDocs(q);

            const updates = snapshot.docs.map(doc =>
                updateDoc(doc.ref, {
                    isRead: true,
                    readAt: new Date().toISOString()
                })
            );

            await Promise.all(updates);
            return true;
        } catch (error) {
            logger.error('[PartnerNotificationRepository] Error marking all as read', error);
            return false;
        }
    }

    async createNotification(data: Omit<PartnerNotificationEntity, 'id' | 'createdAt' | 'updatedAt'>): Promise<string | null> {
        try {
            const docRef = await addDoc(this.collection, {
                ...this.toDocument(data),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            return docRef.id;
        } catch (error) {
            logger.error('[PartnerNotificationRepository] Error creating notification', error);
            return null;
        }
    }

    notifyNewOrder(partnerId: string, orderId: string, total: number): Promise<string | null> {
        return this.createNotification({
            partnerId,
            type: 'new_order',
            title: 'Nueva orden recibida!',
            message: `Tienes una nueva orden por $${total.toLocaleString('es-AR')}`,
            data: { orderId, amount: total },
            isRead: false,
            isUrgent: true
        });
    }

    notifyProductApproved(partnerId: string, productId: string, productTitle: string): Promise<string | null> {
        return this.createNotification({
            partnerId,
            type: 'product_approved',
            title: 'Producto aprobado',
            message: `"${productTitle}" ha sido aprobado y está visible en el catálogo`,
            data: { productId },
            isRead: false,
            isUrgent: false
        });
    }

    notifyProductRejected(partnerId: string, productId: string, productTitle: string, reason?: string): Promise<string | null> {
        return this.createNotification({
            partnerId,
            type: 'product_rejected',
            title: 'Producto rechazado',
            message: `"${productTitle}" no fue aprobado${reason ? `: ${reason}` : ''}`,
            data: { productId, reason },
            isRead: false,
            isUrgent: true
        });
    }

    notifyReviewReceived(partnerId: string, productId: string, productTitle: string, rating: number): Promise<string | null> {
        return this.createNotification({
            partnerId,
            type: 'review_received',
            title: 'Nueva reseña',
            message: `"${productTitle}" recibió una reseña de ${rating} estrellas`,
            data: { productId, rating },
            isRead: false,
            isUrgent: false
        });
    }

    notifyQuestionReceived(partnerId: string, productId: string, productTitle: string): Promise<string | null> {
        return this.createNotification({
            partnerId,
            type: 'question_received',
            title: 'Nueva pregunta',
            message: `Tienes una nueva pregunta sobre "${productTitle}"`,
            data: { productId },
            isRead: false,
            isUrgent: false
        });
    }

    notifyLowStock(partnerId: string, productId: string, productTitle: string, stock: number): Promise<string | null> {
        return this.createNotification({
            partnerId,
            type: 'low_stock',
            title: 'Stock bajo',
            message: `"${productTitle}" tiene solo ${stock} unidades disponibles`,
            data: { productId, stock },
            isRead: false,
            isUrgent: true
        });
    }

    notifyCommissionPaid(partnerId: string, amount: number): Promise<string | null> {
        return this.createNotification({
            partnerId,
            type: 'commission_paid',
            title: 'Pago recibido',
            message: `Has recibido un pago de $${amount.toLocaleString('es-AR')}`,
            data: { amount },
            isRead: false,
            isUrgent: false
        });
    }
}

export const partnerNotificationRepository = new PartnerNotificationRepository();
